import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Shield, FileText, AlertTriangle, CheckCircle, RefreshCw, Loader2, Receipt, Clock, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuditReport = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [reports, setReports] = useState([]);
  const [currentReport, setCurrentReport] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  const years = [2026, 2025, 2024, 2023].map(y => ({ value: y.toString(), label: y.toString() }));

  useEffect(() => { fetchReports(); }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/reports/audit-protection`, {
        withCredentials: true
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setReports(data);
      if (data.length > 0) setCurrentReport(data[0]);
    } catch (error) {
      // No reports yet - that's ok
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/reports/audit-protection`,
        { year: parseInt(selectedYear) },
        { withCredentials: true }
      );
      setCurrentReport(response.data);
      fetchReports();
      toast.success(t('toast.audit_report_generated'));
    } catch (error) {
      toast.error(t('toast.failed_to_generate_report'));
    } finally {
      setGenerating(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-50 border-red-200';
      case 'medium': return 'bg-amber-50 border-amber-200';
      default: return 'bg-yellow-50 border-yellow-200';
    }
  };

  // ── Commandment II: readiness score/grade computed in backend, not here ──
  // Backend returns report.readiness_score (0-100) and report.readiness_grade (A-F)
  // Only the display color mapping (UI concern) remains on the client:
  const getReadinessColor = (score) => {
    if (score >= 90) return 'bg-green-500';
    if (score >= 75) return 'bg-green-400';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const formatCurrency = (amt) => `$${(amt || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="audit-report-page">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Audit Protection Report</h1>
            <p className="text-sm text-gray-600">Assess your audit readiness and identify potential risks</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-32" data-testid="audit-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleGenerateReport} disabled={generating} data-testid="generate-audit-btn">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Generate Report
            </Button>
          </div>
        </div>

        {loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="text-gray-500 mt-2">Loading reports...</p>
            </CardContent>
          </Card>
        ) : !currentReport ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Shield className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No Audit Reports Yet</h3>
              <p className="text-gray-500 mb-4">Generate your first audit protection report to assess your readiness</p>
              <Button onClick={handleGenerateReport} disabled={generating} data-testid="first-generate-btn">
                Generate Report for {selectedYear}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Score Card */}
            {(() => {
              // ── Commandment II: score & grade are backend DTO fields ──
              const score = currentReport.readiness_score ?? 0;
              const grade = currentReport.readiness_grade ?? '?';
              const color = getReadinessColor(score);
              return (
                <div className="grid md:grid-cols-3 gap-6">
                  <Card className="md:col-span-1">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <div className={`w-24 h-24 rounded-full ${color} flex items-center justify-center mx-auto mb-4`}>
                          <span className="text-4xl font-bold text-white">{grade}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">Audit Readiness Score</p>
                        <p className="text-3xl font-bold">{score}/100</p>
                        <p className="text-xs text-gray-500 mt-2">
                          Generated: {new Date(currentReport.generated_at).toLocaleDateString()}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="md:col-span-2">
                    <CardHeader>
                      <CardTitle className="text-lg">Financial Summary - {currentReport.year}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-3 bg-green-50 rounded-lg">
                          <p className="text-xs text-gray-600">Total Income</p>
                          <p className="text-lg font-bold text-green-700">{formatCurrency(currentReport.summary?.gross_income)}</p>
                        </div>
                        <div className="p-3 bg-red-50 rounded-lg">
                          <p className="text-xs text-gray-600">Total Expenses</p>
                          <p className="text-lg font-bold text-red-700">{formatCurrency(currentReport.summary?.total_expenses)}</p>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <p className="text-xs text-gray-600">Net Income</p>
                          <p className="text-lg font-bold text-blue-700">{formatCurrency(currentReport.summary?.net_income)}</p>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <p className="text-xs text-gray-600">Receipt Count</p>
                          <p className="text-lg font-bold text-purple-700">{currentReport.summary?.receipt_count || 0}</p>
                        </div>
                      </div>
                      <div className="flex gap-4 mt-4 text-sm text-gray-600 flex-wrap">
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {currentReport.summary?.transaction_count || 0} transactions
                        </span>
                        <span className="flex items-center gap-1">
                          <Receipt className="w-4 h-4" />
                          {currentReport.summary?.receipt_count || 0} receipts
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="w-4 h-4" />
                          {currentReport.summary?.deduction_count || 0} deductions
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })()}

            {/* Audit Flags / Risk Factors */}
            {currentReport.audit_flags && currentReport.audit_flags.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Risk Factors Identified ({currentReport.audit_flags.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {currentReport.audit_flags.map((flag, i) => (
                      <div key={i} className={`p-4 rounded-lg border ${getSeverityColor(flag.severity)}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium capitalize">{flag.type?.replace(/_/g, ' ')}</span>
                          <Badge variant={flag.severity === 'high' ? 'destructive' : 'secondary'}>
                            {flag.severity?.toUpperCase()} RISK
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600">{flag.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-emerald-200">
                <CardContent className="py-8 text-center">
                  <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="text-lg font-semibold text-emerald-800">No Audit Red Flags</h3>
                  <p className="text-emerald-600 text-sm">Your financial records look clean for {currentReport.year}</p>
                </CardContent>
              </Card>
            )}

            {/* Recommendations */}
            {currentReport.recommendations && currentReport.recommendations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                    Recommendations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {currentReport.recommendations.map((rec, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-sm font-medium text-emerald-700">{i + 1}</span>
                        </div>
                        <span className="text-gray-700">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* IRS Statute Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5 text-blue-500" />
                  IRS Statute of Limitations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="font-medium text-blue-800">Standard</p>
                    <p className="text-sm text-blue-600 mt-1">3 years from filing date</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <p className="font-medium text-amber-800">Substantial Understatement</p>
                    <p className="text-sm text-amber-600 mt-1">6 years if income understated by 25%+</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg">
                    <p className="font-medium text-red-800">Fraud</p>
                    <p className="text-sm text-red-600 mt-1">No time limit for fraudulent returns</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Previous Reports */}
            {reports.length > 1 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Previous Reports</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {reports.slice(1).map((report) => {
                      // ── Commandment II: score & grade from backend DTO ──
                      const rScore = report.readiness_score ?? 0;
                      const rGrade = report.readiness_grade ?? '?';
                      const rColor = getReadinessColor(rScore);
                      return (
                        <div
                          key={report.report_id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                          onClick={() => setCurrentReport(report)}
                          data-testid={`prev-report-${report.report_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full ${rColor} flex items-center justify-center`}>
                              <span className="text-sm font-bold text-white">{rGrade}</span>
                            </div>
                            <div>
                              <p className="font-medium">Tax Year {report.year}</p>
                              <p className="text-sm text-gray-500">{new Date(report.generated_at).toLocaleDateString()}</p>
                            </div>
                          </div>
                          <span className="text-lg font-bold text-gray-700">{rScore}/100</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AuditReport;
